
import React, { useEffect, useState } from 'react';
import { Card, Button, Input } from '../components/UI';
import { Trophy, Star, Crown, Medal, Plus, Trash2, X, Car, CheckCircle, ArrowUp, ArrowDown, Database, Edit2, Users, Keyboard } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole } from '../types';
import { API_BASE_URL } from '../config';

interface Legend {
    id: string;
    season_name: string;
    year: number;
    driver_id: string;
    driver_text?: string;
    driver_name: string;
    avatar_url?: string;
    // Constructor Champion Data
    team_id: string; // Raw ID from DB
    constructor_name: string;
    constructor_color: string;
    constructor_logo?: string;
    constructor_car?: string;
    // Driver Team Data
    driver_team_id?: string; // Raw ID from DB
    driver_team_name: string;
    driver_team_color: string;
    
    // Constructor Drivers
    constructor_driver1_id?: string;
    constructor_driver1_text?: string;
    constructor_driver1_name?: string;
    constructor_driver2_id?: string;
    constructor_driver2_text?: string;
    constructor_driver2_name?: string;
    
    points: number;
    sort_order: number;
}

export const HallOfFame: React.FC = () => {
    const { user } = useAuth();
    const { drivers, teams } = useLeague();
    const [legends, setLegends] = useState<Legend[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    
    // Form State
    const [editingId, setEditingId] = useState<string | null>(null);
    const [newEntry, setNewEntry] = useState({
        seasonName: '',
        year: new Date().getFullYear(),
        driverId: '',
        driverText: '',
        driverTeamId: '',
        constructorTeamId: '',
        constructorDriver1Id: '',
        constructorDriver1Text: '',
        constructorDriver2Id: '',
        constructorDriver2Text: '',
        points: 0
    });

    // Toggle states for input mode
    const [manualDriver, setManualDriver] = useState(false);
    const [manualCD1, setManualCD1] = useState(false);
    const [manualCD2, setManualCD2] = useState(false);

    const isAdmin = user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN);
    const isWebAdmin = user?.roles.includes(UserRole.WEBADMIN);

    const loadData = () => {
        setLoading(true);
        apiRequest<Legend[]>('hall_of_fame.php')
            .then(res => setLegends(Array.isArray(res) ? res : []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadData();
    }, []);

    const resetForm = () => {
        setNewEntry({ seasonName: '', year: new Date().getFullYear(), driverId: '', driverText: '', driverTeamId: '', constructorTeamId: '', constructorDriver1Id: '', constructorDriver1Text: '', constructorDriver2Id: '', constructorDriver2Text: '', points: 0 });
        setEditingId(null);
        setManualDriver(false);
        setManualCD1(false);
        setManualCD2(false);
    };

    const handleOpenCreate = () => {
        resetForm();
        setShowModal(true);
    };

    const handleEdit = (legend: Legend) => {
        // Check if ID is missing but Text is present to set manual mode
        const isManDriver = !legend.driver_id && !!legend.driver_name; // driver_name from API contains COALESCE result
        const isManCD1 = !legend.constructor_driver1_id && !!legend.constructor_driver1_name;
        const isManCD2 = !legend.constructor_driver2_id && !!legend.constructor_driver2_name;

        setManualDriver(isManDriver);
        setManualCD1(isManCD1);
        setManualCD2(isManCD2);

        setNewEntry({
            seasonName: legend.season_name,
            year: legend.year,
            driverId: legend.driver_id || '',
            driverText: isManDriver ? legend.driver_name : '',
            driverTeamId: legend.driver_team_id || '', 
            constructorTeamId: legend.team_id,
            constructorDriver1Id: legend.constructor_driver1_id || '',
            constructorDriver1Text: isManCD1 ? legend.constructor_driver1_name || '' : '',
            constructorDriver2Id: legend.constructor_driver2_id || '',
            constructorDriver2Text: isManCD2 ? legend.constructor_driver2_name || '' : '',
            points: legend.points
        });
        setEditingId(legend.id);
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!newEntry.seasonName || !newEntry.constructorTeamId || !newEntry.driverTeamId) {
            alert("Bitte fülle alle Pflichtfelder aus (Saison, Team Fahrer, Team KWM).");
            return;
        }
        
        // Validation logic based on mode
        if (manualDriver && !newEntry.driverText) { alert("Fahrername fehlt."); return; }
        if (!manualDriver && !newEntry.driverId) { alert("Fahrer fehlt."); return; }

        try {
            // Prepare payload: clear fields that are not relevant for the current mode
            const payload = { ...newEntry };
            if (manualDriver) payload.driverId = ''; else payload.driverText = '';
            if (manualCD1) payload.constructorDriver1Id = ''; else payload.constructorDriver1Text = '';
            if (manualCD2) payload.constructorDriver2Id = ''; else payload.constructorDriver2Text = '';

            if (editingId) {
                // Update
                await apiRequest('hall_of_fame.php', 'PUT', { id: editingId, ...payload });
            } else {
                // Create
                await apiRequest('hall_of_fame.php', 'POST', payload);
            }
            loadData();
            setShowModal(false);
            resetForm();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleDelete = async (id: string) => {
        if(!confirm("Eintrag wirklich löschen?")) return;
        try {
            await apiRequest(`hall_of_fame.php?id=${id}`, 'DELETE');
            loadData();
        } catch(e: any) {
            alert(e.message);
        }
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        const current = legends[index];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (targetIndex < 0 || targetIndex >= legends.length) return;
        
        const target = legends[targetIndex];

        if (current.year !== target.year) return;

        const newLegends = [...legends];
        newLegends[index] = target;
        newLegends[targetIndex] = current;
        
        const tempOrder = current.sort_order;
        current.sort_order = target.sort_order;
        target.sort_order = tempOrder;

        setLegends(newLegends);

        try {
            await apiRequest('hall_of_fame.php', 'PUT', {
                action: 'reorder',
                items: [
                    { id: current.id, sort_order: current.sort_order },
                    { id: target.id, sort_order: target.sort_order }
                ]
            });
        } catch (e) {
            console.error("Sort Error", e);
            loadData(); 
        }
    };

    const onDriverSelect = (driverId: string) => {
        const driverObj = drivers.find(d => d.id === driverId);
        let suggestedTeam = '';
        if (driverObj && driverObj.teamId) {
            suggestedTeam = driverObj.teamId;
        }
        setNewEntry(prev => ({ ...prev, driverId: driverId, driverTeamId: suggestedTeam || prev.driverTeamId }));
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        if (baseUrl && !url.startsWith('/')) return `${baseUrl}/${url}`;
        if (baseUrl) return `${baseUrl}${url}`;
        return url;
    };

    const getDbUpdateUrl = () => {
        const baseUrl = API_BASE_URL === '.' ? window.location.origin + window.location.pathname.replace(/\/+$/, '') : API_BASE_URL;
        return `${baseUrl}/php/update_db.php`;
    };

    // Helper Component for Input Toggle
    const InputToggle = ({ label, isManual, onToggle }: { label: string, isManual: boolean, onToggle: () => void }) => (
        <div className="flex justify-between items-center mb-1">
            <label className="text-[10px] text-gray-500 uppercase font-bold">{label}</label>
            <label className="flex items-center gap-1 cursor-pointer select-none">
                <input type="checkbox" checked={isManual} onChange={onToggle} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-f1-red focus:ring-0"/>
                <span className="text-[10px] text-gray-400 font-bold flex items-center gap-1"><Keyboard size={10}/> Freitext</span>
            </label>
        </div>
    );

    return (
        <div className="sdc-feature-page sdc-hall-page max-w-7xl mx-auto space-y-8 animate-fade-in pb-20">
            <PageHero
                eyebrow="SDC Legacy"
                title="Hall of Fame"
                description="Die Champions und Konstrukteurs-Weltmeister, die ihre Spuren in der Geschichte des Sunday Driver Cup hinterlassen haben."
                icon={Crown}
                accent="#eab308"
                stats={[
                    { label: 'Champions', value: legends.length },
                    { label: 'Jahre', value: new Set(legends.map(l => l.year)).size },
                    { label: 'Neuester Titel', value: legends[0]?.year || '–' },
                ]}
                actions={(
                    <div className="flex flex-wrap gap-2">
                    {isAdmin && (
                        <Button onClick={handleOpenCreate} icon={Plus} className="bg-[#B8860B] hover:bg-[#8B6508] border-none text-white shadow-lg">
                            Champion ergänzen
                        </Button>
                    )}
                    {isWebAdmin && (
                        <a href={getDbUpdateUrl()} target="_blank" rel="noreferrer" className="inline-block">
                            <Button size="sm" variant="secondary" icon={Database} className="w-full bg-gray-800 hover:bg-gray-700 text-xs">
                                Datenbank-Patch
                            </Button>
                        </a>
                    )}
                    </div>
                )}
            />

            {loading ? (
                <div className="text-center p-20 text-gray-500 font-mono">Historie wird geladen …</div>
            ) : legends.length === 0 ? (
                <div className="rounded-[2rem] border-2 border-dashed border-white/[0.07] bg-white/[0.02] p-20 text-center text-gray-600">
                    <Trophy size={48} className="mx-auto mb-4 opacity-20"/>
                    <p>Noch keine Legenden eingetragen.</p>
                </div>
            ) : (
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-9">
                    {legends.map((legend, index) => {
                        const prevInYear = legends[index - 1]?.year === legend.year;
                        const nextInYear = legends[index + 1]?.year === legend.year;

                        const drvColor = legend.driver_team_color || legend.constructor_color || '#666';
                        const drvTeamName = legend.driver_team_name || legend.constructor_name || 'Unbekannt';
                        
                        const constColor = legend.constructor_color || '#666';
                        const constName = legend.constructor_name || 'Unbekannt';
                        const constLogo = legend.constructor_logo;
                        const constCar = legend.constructor_car;

                        return (
                            <div key={legend.id} className="group relative">
                                {/* Season Title floating above */}
                                <div className="absolute -top-5 left-6 z-20 rounded-full border border-yellow-300/20 bg-gradient-to-r from-[#B8860B] to-[#8B6508] px-6 py-2 text-xs font-black uppercase tracking-[0.16em] text-white shadow-xl md:left-10 md:text-sm">
                                    {legend.season_name} <span className="opacity-60 ml-2 font-mono font-normal">| {legend.year}</span>
                                </div>

                                {/* Main Card */}
                                <div className="relative overflow-hidden rounded-[2rem] border border-white/[0.075] bg-gradient-to-br from-[#19191e] via-[#12131a] to-[#0d0f15] pt-10 shadow-[0_28px_80px_rgba(0,0,0,0.36)] transition-all hover:-translate-y-1 hover:border-[#B8860B]/50 hover:shadow-[0_28px_90px_rgba(184,134,11,0.12)]">
                                    
                                    {isAdmin && (
                                        <>
                                            <div className="absolute top-4 right-4 z-30 flex gap-2">
                                                <button onClick={() => handleEdit(legend)} className="p-2 bg-gray-800 rounded-full text-gray-500 hover:text-white hover:bg-gray-700 transition-colors">
                                                    <Edit2 size={16}/>
                                                </button>
                                                <button onClick={() => handleDelete(legend.id)} className="p-2 bg-gray-800 rounded-full text-gray-500 hover:text-red-500 hover:bg-gray-700 transition-colors">
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                            
                                            <div className="absolute top-4 left-4 z-30 flex flex-col gap-1">
                                                {prevInYear && (
                                                    <button onClick={() => handleMove(index, 'up')} className="p-1.5 bg-gray-800 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
                                                        <ArrowUp size={14} />
                                                    </button>
                                                )}
                                                {nextInYear && (
                                                    <button onClick={() => handleMove(index, 'down')} className="p-1.5 bg-gray-800 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">
                                                        <ArrowDown size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                        {/* Left: Driver Side */}
                                        <div className="p-8 flex flex-col items-center justify-center relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40 pointer-events-none"></div>
                                            <div className="absolute top-0 left-0 w-full h-1" style={{backgroundColor: drvColor}}></div>
                                            
                                            <div className="relative mb-6">
                                                <div className="w-40 h-40 rounded-full border-4 p-1 shadow-2xl relative z-10" style={{borderColor: drvColor}}>
                                                    <div className="w-full h-full rounded-full overflow-hidden bg-gray-800">
                                                        {legend.avatar_url && legend.driver_id ? ( // Only show avatar if real user link exists
                                                            <img src={getUrl(legend.avatar_url)} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-gray-600">
                                                                {legend.driver_name.charAt(0)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="absolute -bottom-3 -right-3 bg-[#FFD700] text-black w-12 h-12 rounded-full flex items-center justify-center border-4 border-[#12121a] z-20 shadow-lg">
                                                    <Trophy size={20} />
                                                </div>
                                            </div>

                                            <div className="text-center relative z-10">
                                                <div className="text-xs font-bold text-[#B8860B] uppercase tracking-[0.3em] mb-2">Fahrer-Weltmeister</div>
                                                <h2 className="text-4xl font-black uppercase italic text-white leading-none mb-2">{legend.driver_name}</h2>
                                                <div className="inline-flex items-center gap-2 bg-gray-900/80 px-4 py-1 rounded-full border border-gray-700">
                                                    <span className="w-2 h-2 rounded-full" style={{backgroundColor: drvColor}}></span>
                                                    <span className="text-xs font-bold text-gray-300 uppercase">{drvTeamName}</span>
                                                </div>
                                                {legend.points > 0 && (
                                                    <div className="mt-4 font-mono font-bold text-[#B8860B] text-xl">
                                                        {legend.points} <span className="text-xs opacity-50">PTS</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right: Constructor Side */}
                                        <div className="relative bg-[#15151a] border-t md:border-t-0 md:border-l border-gray-800 flex flex-col justify-center overflow-hidden min-h-[300px]">
                                            <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                                                {constLogo && <img src={getUrl(constLogo)} className="w-3/4 object-contain grayscale" />}
                                            </div>
                                            
                                            {constCar && (
                                                <div className="absolute right-[-20%] bottom-[-10%] w-[120%] z-0 opacity-40 md:opacity-60 transform -rotate-6 transition-transform group-hover:scale-105 duration-1000">
                                                    <img src={getUrl(constCar)} className="w-full object-contain drop-shadow-2xl" />
                                                </div>
                                            )}

                                            <div className="relative z-10 p-8 text-center md:text-right flex flex-col items-center md:items-end h-full justify-between">
                                                <div className="text-xs font-bold text-gray-500 uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                                                    Konstrukteurs-Weltmeister <Star size={12} className="text-gray-600"/>
                                                </div>
                                                
                                                <div className="flex flex-col items-center md:items-end">
                                                    {constLogo && (
                                                        <img src={getUrl(constLogo)} className="h-16 md:h-20 object-contain mb-4 drop-shadow-lg" />
                                                    )}
                                                    <h3 className="text-3xl font-black uppercase italic text-white mb-4" style={{color: constColor}}>
                                                        {constName}
                                                    </h3>
                                                    
                                                    {/* Constructor Drivers List */}
                                                    {(legend.constructor_driver1_name || legend.constructor_driver2_name) && (
                                                        <div className="flex flex-col items-end gap-1 mt-auto">
                                                            <div className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Fahrer</div>
                                                            {legend.constructor_driver1_name && (
                                                                <div className="text-sm text-gray-300 font-bold bg-black/40 px-2 py-1 rounded border border-gray-700/50">
                                                                    {legend.constructor_driver1_name}
                                                                </div>
                                                            )}
                                                            {legend.constructor_driver2_name && (
                                                                <div className="text-sm text-gray-300 font-bold bg-black/40 px-2 py-1 rounded border border-gray-700/50">
                                                                    {legend.constructor_driver2_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* CREATE/EDIT MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <Card className="w-full max-w-md bg-[#1a1a20] border-gray-600 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold uppercase italic text-white">
                                {editingId ? 'Eintrag bearbeiten' : 'Champion ergänzen'}
                            </h3>
                            <button onClick={() => setShowModal(false)}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <Input 
                                label="Saisonname" 
                                value={newEntry.seasonName} 
                                onChange={e => setNewEntry({...newEntry, seasonName: e.target.value})} 
                                placeholder="e.g. Season 5"
                            />
                            <Input 
                                label="Jahr" 
                                type="number"
                                value={newEntry.year} 
                                onChange={e => setNewEntry({...newEntry, year: parseInt(e.target.value)})} 
                            />
                            
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase text-[#B8860B]">Fahrer-Weltmeister</label>
                                <div className="space-y-2">
                                    <div>
                                        <InputToggle label="Champion Driver" isManual={manualDriver} onToggle={() => { setManualDriver(!manualDriver); setNewEntry(prev => ({...prev, driverId: '', driverText: ''})); }} />
                                        
                                        {manualDriver ? (
                                            <input
                                                className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                                placeholder="Name eingeben (z.B. Michael Schumacher)"
                                                value={newEntry.driverText}
                                                onChange={e => setNewEntry({...newEntry, driverText: e.target.value})}
                                            />
                                        ) : (
                                            <select 
                                                className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                                value={newEntry.driverId}
                                                onChange={e => onDriverSelect(e.target.value)}
                                            >
                                                <option value="">-- Fahrer wählen --</option>
                                                {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                            </select>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-500 uppercase font-bold">Team des Fahrers</label>
                                        <select 
                                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                            value={newEntry.driverTeamId}
                                            onChange={e => setNewEntry({...newEntry, driverTeamId: e.target.value})}
                                        >
                                            <option value="">-- Team wählen --</option>
                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <Input 
                                        label="Gesamtpunkte" 
                                        type="number"
                                        value={newEntry.points} 
                                        onChange={e => setNewEntry({...newEntry, points: parseInt(e.target.value)})} 
                                    />
                                </div>
                            </div>

                            <div className="border-t border-gray-700 my-2 pt-2">
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase text-[#B8860B]">Konstrukteurs-Weltmeister</label>
                                <div className="space-y-2">
                                    <div>
                                        <label className="text-[10px] text-gray-500 uppercase font-bold">Siegerteam</label>
                                        <select 
                                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                            value={newEntry.constructorTeamId}
                                            onChange={e => setNewEntry({...newEntry, constructorTeamId: e.target.value})}
                                        >
                                            <option value="">-- Team wählen --</option>
                                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <InputToggle label="Driver 1" isManual={manualCD1} onToggle={() => { setManualCD1(!manualCD1); setNewEntry(prev => ({...prev, constructorDriver1Id: '', constructorDriver1Text: ''})); }} />
                                            {manualCD1 ? (
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs"
                                                    placeholder="Name"
                                                    value={newEntry.constructorDriver1Text}
                                                    onChange={e => setNewEntry({...newEntry, constructorDriver1Text: e.target.value})}
                                                />
                                            ) : (
                                                <select 
                                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs"
                                                    value={newEntry.constructorDriver1Id}
                                                    onChange={e => setNewEntry({...newEntry, constructorDriver1Id: e.target.value})}
                                                >
                                                    <option value="">-- Driver 1 --</option>
                                                    {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                                </select>
                                            )}
                                        </div>
                                        <div>
                                            <InputToggle label="Driver 2" isManual={manualCD2} onToggle={() => { setManualCD2(!manualCD2); setNewEntry(prev => ({...prev, constructorDriver2Id: '', constructorDriver2Text: ''})); }} />
                                            {manualCD2 ? (
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs"
                                                    placeholder="Name"
                                                    value={newEntry.constructorDriver2Text}
                                                    onChange={e => setNewEntry({...newEntry, constructorDriver2Text: e.target.value})}
                                                />
                                            ) : (
                                                <select 
                                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs"
                                                    value={newEntry.constructorDriver2Id}
                                                    onChange={e => setNewEntry({...newEntry, constructorDriver2Id: e.target.value})}
                                                >
                                                    <option value="">-- Driver 2 --</option>
                                                    {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-700">
                            <Button variant="ghost" onClick={() => setShowModal(false)}>Abbrechen</Button>
                            <Button onClick={handleSave} icon={CheckCircle}>
                                {editingId ? 'Eintrag speichern' : 'Zur Historie hinzufügen'}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
